import { ParticleSystem, type Particle } from './ParticleSystem';
import type { WaterGrid } from '../water/WaterGrid';
import type { SandGrid } from '../sandgrid/SandGrid';

export class FoamParticleSystem extends ParticleSystem {
  private emissionRate: number = 0.5; // Particles per frame per high-velocity cell
  private velocityThreshold: number = 2.0; // Minimum velocity to emit foam

  constructor(maxParticles: number = 500) {
    super(maxParticles);
  }

  public update(deltaTime: number): void {
    super.update(deltaTime);

    // Apply gravity and drag to foam particles
    for (const p of this.particles) {
      // Gravity (float upward for foam)
      p.vz += 5.0 * deltaTime;

      // Drag
      p.vx *= 0.95;
      p.vy *= 0.95;
      p.vz *= 0.92;
    }
  }

  public emitFromWater(
    waterGrid: WaterGrid,
    sandGrid: SandGrid,
    deltaTime: number
  ): void {
    const width = waterGrid.width;
    const height = waterGrid.height;

    // Sample water grid for high-velocity areas
    // Water depth is stored as relative height (depth above sand)
    for (let y = 0; y < height; y += 2) {
      // Skip every other cell for performance
      for (let x = 0; x < width; x += 2) {
        const waterDepth = waterGrid.getHeight(x, y);
        const sandHeight = sandGrid.get(x, y);

        // Only emit where there's significant water depth
        if (waterDepth <= 0.1) continue;

        // Check velocity
        const [vx, vy] = waterGrid.getVelocity(x, y);
        const velocity = Math.sqrt(vx * vx + vy * vy);

        // Emit particles based on velocity
        if (velocity > this.velocityThreshold) {
          const emitChance = (velocity / 10) * this.emissionRate * deltaTime;

          if (Math.random() < emitChance) {
            // Calculate water surface position (sand height + water depth)
            const waterSurfaceHeight = sandHeight + waterDepth;
            this.emitFoamParticle(
              x - width / 2,
              waterSurfaceHeight,
              y - height / 2,
              vx,
              vy
            );
          }
        }
      }
    }
  }

  private emitFoamParticle(
    x: number,
    z: number,
    y: number,
    vx: number,
    vy: number
  ): void {
    const particle: Particle = {
      x: x + (Math.random() - 0.5),
      y: y + (Math.random() - 0.5),
      z: z + Math.random() * 0.5,
      vx: vx * 0.3 + (Math.random() - 0.5) * 2,
      vy: vy * 0.3 + (Math.random() - 0.5) * 2,
      vz: Math.random() * 3 + 1,
      life: 0.5 + Math.random() * 1.0,
      maxLife: 0.5 + Math.random() * 1.0,
      size: 0.3 + Math.random() * 0.4,
      r: 0.95,
      g: 0.98,
      b: 1.0,
      alpha: 0.8,
    };

    this.emit(particle);
  }
}
